/**
 * confirmDestructive — Sprint 2 (F5E-006)
 *
 * Modal de confirmación estilo GitHub: requiere que el admin tipee un texto
 * exacto (ej: nombre del recurso) para habilitar el botón destructivo.
 *
 * Uso:
 *   const ok = await confirmDestructive({
 *     title: 'Eliminar propiedad',
 *     message: 'Esta acción es irreversible.',
 *     confirmText: 'ELIMINAR',
 *     danger: true,
 *   });
 *   if (!ok) return;
 */

import Swal from 'sweetalert2';

export interface ConfirmDestructiveOptions {
  title: string;
  message: string;
  /** Texto exacto que el usuario debe tipear */
  confirmText: string;
  /** Etiqueta del botón de confirmación. Default: "Sí, ejecutar" */
  buttonText?: string;
  /** Si true usa estilo rojo. Default: true */
  danger?: boolean;
}

export async function confirmDestructive(
  options: ConfirmDestructiveOptions
): Promise<boolean> {
  const {
    title,
    message,
    confirmText,
    buttonText = 'Sí, ejecutar',
    danger = true,
  } = options;

  const result = await Swal.fire({
    title,
    html: `
      <p>${message}</p>
      <p style="margin-top:12px;">Para confirmar, escribe <strong>${confirmText}</strong>:</p>
      <input id="swal-confirm-input" class="swal2-input" autocomplete="off"
             aria-label="Confirmación de operación destructiva" />
    `,
    icon: danger ? 'warning' : 'question',
    showCancelButton: true,
    confirmButtonText: buttonText,
    cancelButtonText: 'Cancelar',
    confirmButtonColor: danger ? '#d33' : '#3085d6',
    focusConfirm: false,
    didOpen: () => {
      const btn = Swal.getConfirmButton();
      const input = document.getElementById('swal-confirm-input') as HTMLInputElement | null;
      if (!btn || !input) return;
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      input.focus();
      input.addEventListener('input', () => {
        const match = input.value.trim() === confirmText;
        btn.disabled = !match;
        btn.setAttribute('aria-disabled', match ? 'false' : 'true');
      });
    },
    preConfirm: () => {
      const input = document.getElementById('swal-confirm-input') as HTMLInputElement | null;
      const value = input?.value?.trim() || '';
      if (value !== confirmText) {
        Swal.showValidationMessage(`Debes tipear exactamente: ${confirmText}`);
        return false;
      }
      return true;
    },
  });

  return result.isConfirmed;
}

export default confirmDestructive;
